// src/PhotoScreen.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Send, X } from "lucide-react";
import FormModal from "./components/FormModal";
import cameraManager from "./cameraManager";
import axios from "axios";
import { useUser } from "./context/UserContext";

function dataURLtoFile(dataurl, filename) {
  let arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export default function PhotoScreen() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  // marca o instante em que o usuário entrou na tela
  const [startTime] = useState(() => Date.now());
  const [hasCameraAccess, setHasCameraAccess] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Driver ID pré-preenchido, se vier da navegação
  const prefillDriverId = location.state?.prefillDriverId || "";

  useEffect(() => {
    if (!photoData) {
      cameraManager.start(videoRef.current).then((success) => {
        setHasCameraAccess(success);
      });
    }
    return () => {
      cameraManager.stop();
    };
  }, [photoData]);

  // Envia a duração para o backend
  const sendDurationMetric = async (driverId) => {
    if (!user) return;
    try {
      const durationSeconds = (Date.now() - startTime) / 1000;
      await axios.post("/api/photo-duration", {
        driver_id: driverId || "",
        duration_seconds: durationSeconds,
      }, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('app_token')}`
        }
      });
    } catch (err) {
      console.error("Falha ao enviar métricas de duração:", err);
    }
  };

  const cleanupAndNavigate = useCallback(
    async (path, options, driverIdForMetric) => {
      cameraManager.stop();
      await sendDurationMetric(driverIdForMetric);
      navigate(path, options);
    },
    [navigate, startTime, user]
  );

  const handleTakePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/png");
    setPhotoData(imageData);
    cameraManager.stop();
  };

  const handleRetakePhoto = () => {
    setPhotoData(null);
  };

  const handleFormSubmit = async (formData) => {
    if (!user) {
      alert("Sessão expirada. Faça o login novamente.");
      cleanupAndNavigate("/", {}, formData.driverId);
      return;
    }
    setIsLoading(true);

    sessionStorage.setItem("lastDriverId", formData.driverId);

    const SUBMIT_URL = "/api/submit-photo";
    const imageFile = dataURLtoFile(photoData, "capture.png");

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", imageFile);
      uploadFormData.append("ops_id", String(user.id));
      uploadFormData.append("driver_id", String(formData.driverId));
      uploadFormData.append("to_id", String(formData.toField));
      uploadFormData.append("placa", "");


      const response = await axios.post(SUBMIT_URL, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result = response.data;

      if (result && result.validation_result) {
        if (result.validation_result.is_valid) {
          sessionStorage.removeItem("lastDriverId");
          cleanupAndNavigate("/success", {}, formData.driverId);
        } else {
          cleanupAndNavigate(
            "/error",
            {
              state: {
                driverId: formData.driverId,
                errorType: "validation",
              },
            },
            formData.driverId
          );
        }
      } else {
        throw new Error("Resposta inesperada do servidor.");
      }
    } catch (error) {
      console.error(
        "Ocorreu um erro no processo de envio:",
        error.response ? error.response.data : error.message
      );
      cleanupAndNavigate(
        "/error",
        {
          state: {
            driverId: formData.driverId,
            errorType: "connection",
          },
        },
        formData.driverId
      );
    } finally {
      setIsLoading(false);
      setIsFormVisible(false);
    }
  };

  if (hasCameraAccess === false) {
    return (
      <div className="w-screen h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <button
          onClick={() =>
            cleanupAndNavigate("/home", { state: { fromPhoto: true } }, "")
          }
          className="absolute top-6 left-4 w-12 h-12 flex items-center justify-center bg-white bg-opacity-70 text-black rounded-full shadow-md"
        >
          <ArrowLeft size={24} />
        </button>
        <p className="text-xl text-center">Permissão da câmera negada.</p>
      </div>
    );
  }

  return (
    <div className="relative w-screen h-screen bg-black overflow-hidden">
      {photoData ? (
        <>
          <img
            src={photoData}
            alt="Foto capturada"
            className="w-full h-full object-cover"
          />
          <button
            onClick={handleRetakePhoto}
            className="absolute top-6 left-4 w-12 h-12 flex items-center justify-center bg-white bg-opacity-70 text-black rounded-full shadow-md"
          >
            <X size={24} />
          </button>
          <button
            onClick={() => setIsFormVisible(true)}
            className="absolute bottom-10 right-6 w-20 h-20 flex items-center justify-center bg-[#EE4D2D] text-white rounded-full shadow-xl hover:bg-[#D03F2A] transition-colors"
          >
            <Send size={32} />
          </button>
        </>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />
          <div className="absolute inset-0 flex items-center justify-center p-8 pointer-events-none">
            <div className="w-full h-[70%] border-4 border-green-500/70 rounded-3xl shadow-lg"></div>
          </div>
          <button
            onClick={() =>
              cleanupAndNavigate("/home", { state: { fromPhoto: true } }, "")
            }
            className="absolute top-6 left-4 w-12 h-12 flex items-center justify-center bg-white bg-opacity-70 text-black rounded-full shadow-md"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
            <button
              onClick={handleTakePhoto}
              className="w-20 h-20 rounded-full bg-white shadow-lg border-4 border-gray-400 active:scale-95 transition"
            />
          </div>
        </>
      )}
      <FormModal
        isVisible={isFormVisible}
        onClose={() => setIsFormVisible(false)}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
        initialDriverId={prefillDriverId}
      />
    </div>
  );
}
